<template>
  <b-container fluid>
    <b-modal v-model="displayDeletion" title="Confirm Deletion" @ok="onConfirmDeletion">
      Are you sure you want to delete this item?
    </b-modal>

    <b-modal id="map-select-modal" size="xl" v-model="displayMapModal" @shown="onMapModalShown"
      @hidden="onMapModalHidden" hide-footer
      :title="mapModalMode === 'add' ? 'Add New Landmark' : 'Select Landmark from Map'">
      <div v-if="mapModalMode === 'add'">
        <LandmarkForm
          :data="newLandmark"
          :errors="landmarkErrors"
          :isSubmit="isSubmittingLandmark"
          :referenceLandmarks="landmarks"
          @onSubmitClicked="onLandmarkCreated"
          @onCancelAddClicked="closeLandmarkForm"
        />
      </div>
      <div v-else>
      <div class="mb-3 position-relative">
        <b-form-input
          v-model="mapSearchQuery"
          placeholder="Search landmark name..."
          @focus="showMapSearchDropdown = true"
          @input="onMapSearchInput"
          @blur="onMapSearchBlur"
        />
        <div
          v-if="showMapSearchDropdown && filteredMapSearchLandmarks.length > 0"
          class="map-search-dropdown"
        >
          <button
            v-for="landmark in filteredMapSearchLandmarks"
            :key="landmark.lndmId"
            type="button"
            class="map-search-item"
            @mousedown.prevent="selectLandmarkFromSearch(landmark)"
          >
            {{ landmark.displayName || landmark.lndmName }}
          </button>
        </div>
      </div>
      <div id="landmark-selector-map" class="landmark-selector-map"></div>
      <div class="d-flex justify-content-between align-items-center mt-3">
        <!-- Cuma muncul kalau user emang boleh nambah landmark. -->
        <b-button v-if="canCreateLandmark" variant="outline-primary" size="sm"
          @click="openLandmarkForm">
          + Add Landmark Here
        </b-button>
        <span v-else></span>
        <div>
          <b-button variant="secondary" @click="displayMapModal = false" class="mr-2">Cancel</b-button>
          <b-button variant="primary" @click="onLandmarkSelectedFromMap"
            :disabled="!selectedMapLandmark">Select</b-button>
        </div>
      </div>
      </div>
    </b-modal>

    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayForm">
      <template #modal-title>{{ currentItem.mvygId ? 'Edit' : 'Create' }} Voyage</template>
      <b-form @submit.prevent="onSubmit">
        <b-form-group label="Asset">
          <b-form-select v-model="currentItem.mvygMassId" :options="assets" value-field="massId" text-field="massName"
            :state="errors.mvygMassId ? false : null">
            <template #first>
              <b-form-select-option :value="null" disabled>Select asset</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-invalid-feedback>
            {{ errors.mvygMassId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Voyage Name">
          <b-form-input v-model="currentItem.mvygName" required :state="errors.mvygName ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygName }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Voyage No.">
          <b-form-input v-model="currentItem.mvygNumber" placeholder="e.g. GM03/2026/01" required
            :state="errors.mvygNumber ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygNumber }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Start Time">
          <DateTimePicker v-model="currentItem.mvygStartTime" masks="DD/MM/YYYY, HH:mm"
            :input-class="errors.mvygStartTime ? 'form-control is-invalid' : 'form-control'" />
          <b-form-invalid-feedback :state="errors.mvygStartTime ? false : null">
            {{ errors.mvygStartTime }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="End Time">
          <DateTimePicker v-model="currentItem.mvygEndTime" masks="DD/MM/YYYY, HH:mm"
            :input-class="errors.mvygEndTime ? 'form-control is-invalid' : 'form-control'" />
          <b-form-invalid-feedback :state="errors.mvygEndTime ? false : null">
            {{ errors.mvygEndTime }}
          </b-form-invalid-feedback>
          <small v-if="formDuration" class="text-muted">Duration: {{ formDuration }}</small>
        </b-form-group>
        <b-form-group label="Origin">
          <LandmarkSearchInput
            ref="originInput"
            v-model="currentItem.mvygOriginLndmId"
            :landmarks="landmarks"
            label="Origin"
            :allow-create="canCreateLandmark"
            :state="errors.mvygOriginLndmId ? false : null"
            @select-on-map="openMapSelector('origin')"
            @add-landmark="openLandmarkCreate('origin', $event)"
          />
          <b-form-invalid-feedback :state="errors.mvygOriginLndmId ? false : null">
            {{ errors.mvygOriginLndmId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Destination">
          <LandmarkSearchInput
            ref="destinationInput"
            v-model="currentItem.mvygDestinationLndmId"
            :landmarks="landmarks"
            label="Destination"
            :allow-create="canCreateLandmark"
            :state="errors.mvygDestinationLndmId ? false : null"
            @select-on-map="openMapSelector('destination')"
            @add-landmark="openLandmarkCreate('destination', $event)"
          />
          <b-form-invalid-feedback :state="errors.mvygDestinationLndmId ? false : null">
            {{ errors.mvygDestinationLndmId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Cargo Status">
          <b-form-select v-model="currentItem.mvygCargoStatus" :options="cargoStatusOptions" required
            :state="errors.mvygCargoStatus ? false : null"></b-form-select>
          <b-form-invalid-feedback>
            {{ errors.mvygCargoStatus }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Cargo Load (Kg)">
          <NumberInput v-model="currentItem.mvygCargoLoadKg" class="form-control"
            :class="{ 'is-invalid': !!errors.mvygCargoLoadKg }" :disabled="!currentItem.mvygCargoStatus" />
          <b-form-invalid-feedback>
            {{ errors.mvygCargoLoadKg }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Cargo Type">
          <b-form-input v-model="currentItem.mvygCargoType" :disabled="!currentItem.mvygCargoStatus"
            :state="errors.mvygCargoType ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygCargoType }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Note">
          <b-form-textarea v-model="currentItem.mvygNote" maxlength="255" rows="3"
            :state="errors.mvygNote ? false : null"></b-form-textarea>
          <small class="text-muted d-block text-right">{{ (currentItem.mvygNote || '').length }}/255</small>
          <b-form-invalid-feedback>
            {{ errors.mvygNote }}
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="text-right">
          <b-button type="button" variant="secondary" @click="onCancel" class="mr-2">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="isSubmit">
            <b-spinner small v-if="isSubmit"></b-spinner>
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
    <div class="iq-card mt-3 px-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Voyage</h4>
        </div>
      </div>
      <div class="iq-card-body">
        <b-row class="mb-4">
          <b-col md="4">
            <b-form-group label="Asset" label-for="asset-select">
              <b-form-select id="asset-select" v-model="selectedAsset" :options="assets" value-field="massId"
                text-field="massName" @change="onAssetSelected">
                <template #first>
                  <b-form-select-option :value="null">All Assets</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <VoyageTable
          :items="data"
          :loading="isLoading"
          :fields="fields"
          :pagination="pagination"
          :sort="sort"
          @page-changed="onPageChanged"
          @sort-changed="onSortChanged"
          @refresh="getData"
          @add="onAdd"
          @view="onDetail"
          @edit="onEdit"
          @delete="onDelete"
        />
      </div>
    </div>
  </b-container>
</template>

<script>
/* eslint-disable no-undef, new-cap */
import VoyageMasterSvc from '@/services/VoyageMasterSvc';
import AssetSvc from '@/services/AssetSvc';
import LandmarkSvc from '@/services/LandmarkSvc';
import moment from 'moment';
import { toApiDatetime, fromApiDatetime } from '@/util/apiDatetime';
import { toDuration } from '@/util/date';
import LandmarkSearchInput from '@/components/map/LandmarkSearchInput.vue';
import VoyageTable from '@/views/voyage/components/VoyageTable.vue';
import NumberInput from '@/components/directives/NumberInput.vue';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import LandmarkForm from '@/components/landmark/Form.vue';

export default {
  name: 'Voyage',
  components: {
    LandmarkSearchInput,
    VoyageTable,
    NumberInput,
    DateTimePicker,
    LandmarkForm,
  },
  data() {
    return {
      voyageMaster: new VoyageMasterSvc(),
      data: [],
      isLoading: false,
      displayDeletion: false,
      selectedItem: null,
      displayForm: false,
      isSubmit: false,
      errors: {},
      currentItem: {},
      assets: [],
      selectedAsset: null,
      assetsLoaded: false,
      landmarks: [],
      displayMapModal: false,
      landmarkSelectionTarget: null,
      mapInstance: null,
      markerLayer: null,
      mapLandmarks: [],
      selectedMapLandmark: null,
      // Modal peta bertukar mode, bukan numpuk modal — b-modal bersarang rewel.
      mapModalMode: 'select',
      newLandmark: {},
      landmarkErrors: {},
      isSubmittingLandmark: false,
      pendingLandmarkLatLng: null,
      mapSearchQuery: '',
      showMapSearchDropdown: false,
      mapSearchBlurTimeout: null,
      mapLoading: false,
      mapInitInProgress: false,
      cargoStatusOptions: [
        { value: 0, text: 'No Cargo' },
        { value: 1, text: 'With Cargo' },
      ],
      pagination: {
        current: 1,
        pageSize: 20,
        totalSize: 0,
      },
      fields: [
        { key: 'mvygName', label: 'Name', sortable: true },
        { key: 'mvygNumber', label: 'Number', sortable: true },
        { key: 'mvygMass.massName', label: 'Asset', formatter: (value) => value || '-' },
        { key: 'route', label: 'Route' },
        { key: 'mvygStartTime', label: 'Period', sortable: true },
        { key: 'actions', label: '', class: 'text-right text-nowrap' },
      ],
      sort: { by: 'mvygStartTime', desc: true },
    };
  },
  computed: {
    formDuration() {
      const { mvygStartTime, mvygEndTime } = this.currentItem;
      if (!mvygStartTime || !mvygEndTime) return '';
      const seconds = moment(mvygEndTime).diff(mvygStartTime, 'seconds');
      return seconds > 0 ? toDuration(seconds) : '';
    },
    canCreateLandmark() {
      const access = this.$store.getters['auth/userAccess'] || {};
      return !!access.master_landmark?.includes('create');
    },
    filteredMapSearchLandmarks() {
      const query = (this.mapSearchQuery || '').trim().toLowerCase();
      if (!query) {
        return this.landmarks.slice(0, 10);
      }
      return this.landmarks
        .filter((landmark) => {
          const name = (landmark.lndmName || '').toLowerCase();
          const displayName = (landmark.displayName || '').toLowerCase();
          return name.includes(query) || displayName.includes(query);
        })
        .slice(0, 10);
    },
  },
  methods: {
    async getLandmarks() {
      try {
        const compId = this.$store.getters['auth/activeCompany'].compId;
        const [companyLandmarksRes, globalLandmarksRes] = await Promise.all([
          LandmarkSvc.landmarks.get({ params: { compId, max: 1000 } }),
          LandmarkSvc.landmarks.get({ params: { max: 1000 } }),
        ]);

        const allLandmarks = new Map();

        (globalLandmarksRes.data.data || []).forEach((l) => {
          allLandmarks.set(l.lndmId, { ...l, displayName: `[GLOBAL] ${l.lndmName}` });
        });

        (companyLandmarksRes.data.data || []).forEach((l) => {
          allLandmarks.set(l.lndmId, { ...l, displayName: `[COMPANY] ${l.lndmName}` });
        });

        this.landmarks = Array.from(allLandmarks.values());
      } catch (e) {
        console.error('get landmarks error : ', e?.message);
        this.$toast.error('Failed to load landmarks');
      }
    },
    async getAssets() {
      try {
        const { data } = await AssetSvc.getAssets();
        this.assets = data.data;

        this.assetsLoaded = true;

        // Honor an asset filter passed via the route query (e.g. the Voyage
        // button on the Home sidebar), otherwise load all voyages.
        const queryAsset = this.$route.query.asset;
        const matched = queryAsset !== undefined && queryAsset !== null && queryAsset !== ''
          ? this.assets.find((asset) => String(asset.massId) === String(queryAsset))
          : null;

        if (matched) {
          // Changing selectedAsset triggers its watcher, which fetches the data.
          this.selectedAsset = matched.massId;
        } else {
          // No filter: selectedAsset stays null so its watcher won't fire; fetch here.
          this.selectedAsset = null;
          this.getData();
        }
      } catch (e) {
        console.error('get assets error : ', e?.message);
        this.$toast.error('Failed to load assets');
      }
    },
    async getData() {
      try {
        this.isLoading = true;
        const params = {
          page: this.pagination.current,
          max: this.pagination.pageSize,
          [`orders[${this.sort.by}]`]: this.sort.desc ? 'desc' : 'asc',
        };

        if (this.selectedAsset) {
          params['finds[mvygMassId]'] = this.selectedAsset;
        } else if (this.assets.length > 0) {
          // When "All Assets" is selected, send all asset IDs as array
          // Format: finds[mvygMassId][]=68&finds[mvygMassId][]=92
          this.assets.forEach((asset) => {
            if (!params['finds[mvygMassId]']) {
              params['finds[mvygMassId]'] = [];
            }
            params['finds[mvygMassId]'].push(asset.massId);
          });
        }

        const Response = await this.voyageMaster.getAll(params);

        // error datang dengan HTTP 200
        if (Response.status?.error) {
          this.data = [];
          this.pagination.totalSize = 0;
          this.$toast.error(typeof Response.status.message === 'string' && Response.status.message
            ? Response.status.message
            : 'Failed to load voyages.');
          return;
        }

        const Data = Response.data;
        this.pagination.totalSize = Response.pagination.totalData;
        this.data = Data;
      } catch (e) {
        console.error('get data error : ', e?.message);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
      }
    },
    onAssetSelected() {
      this.pagination.current = 1;
      // getData() will be called by the watcher
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    onSortChanged({ sortBy, sortDesc }) {
      if (!sortBy) return;
      this.sort = { by: sortBy, desc: sortDesc };
      this.pagination.current = 1;
      this.getData();
    },
    onAdd() {
      this.errors = {};
      this.currentItem = {
        mvygName: '',
        mvygNumber: '',
        mvygStartTime: moment().toDate(),
        mvygEndTime: moment().add(1, 'day').toDate(),
        mvygOriginLndmId: null,
        originDisplayName: '',
        mvygDestinationLndmId: null,
        destinationDisplayName: '',
        mvygCargoStatus: 0,
        mvygCargoLoadKg: 0,
        mvygCargoType: '-',
        mvygNote: '',
        mvygMassId: this.selectedAsset,
      };
      this.displayForm = true;
    },
    onEdit(item) {
      this.errors = {};
      const origin = this.landmarks.find((l) => l.lndmId === item.mvygOriginLndmId);
      const destination = this.landmarks.find((l) => l.lndmId === item.mvygDestinationLndmId);
      this.currentItem = {
        ...item,
        mvygStartTime: fromApiDatetime(item.mvygStartTime).toDate(),
        mvygEndTime: fromApiDatetime(item.mvygEndTime).toDate(),
        originDisplayName: origin ? origin.displayName : '',
        destinationDisplayName: destination ? destination.displayName : '',
      };
      this.displayForm = true;
    },
    onCancel() {
      this.displayForm = false;
      this.errors = {};
    },
    async onSubmit() {
      this.isSubmit = true;
      this.errors = {};
      // Asset dipilih di form, bukan di filter halaman — jadi ini satu-satunya penjaga.
      if (!this.currentItem.mvygMassId) {
        this.errors.mvygMassId = 'Asset is required.';
        this.$toast.error('Please select an asset.');
        this.isSubmit = false;
        return;
      }
      if (moment(this.currentItem.mvygStartTime).isAfter(this.currentItem.mvygEndTime)) {
        this.errors.mvygStartTime = 'Start Time cannot be after End Time.';
        this.errors.mvygEndTime = 'Start Time cannot be after End Time.';
        this.$toast.error('Please check the form for errors.');
        this.isSubmit = false;
        return;
      }
      if (!this.currentItem.mvygOriginLndmId) {
        this.errors.mvygOriginLndmId = 'Origin is required.';
        this.$toast.error('Please select an origin landmark.');
        this.isSubmit = false;
        return;
      }
      if (!this.currentItem.mvygDestinationLndmId) {
        this.errors.mvygDestinationLndmId = 'Destination is required.';
        this.$toast.error('Please select a destination landmark.');
        this.isSubmit = false;
        return;
      }
      try {
        const payload = {
          mvygName: this.currentItem.mvygName,
          mvygNumber: this.currentItem.mvygNumber,
          mvygStartTime: toApiDatetime(this.currentItem.mvygStartTime),
          mvygEndTime: toApiDatetime(this.currentItem.mvygEndTime),
          mvygOriginLndmId: this.currentItem.mvygOriginLndmId,
          mvygDestinationLndmId: this.currentItem.mvygDestinationLndmId,
          mvygCargoStatus: this.currentItem.mvygCargoStatus,
          mvygCargoLoadKg: this.currentItem.mvygCargoLoadKg,
          mvygCargoType: this.currentItem.mvygCargoType,
          mvygNote: this.currentItem.mvygNote,
          mvygMassId: this.currentItem.mvygMassId,
        };

        const isUpdate = !!this.currentItem.mvygId;
        const response = isUpdate
          ? await this.voyageMaster.update(this.currentItem.mvygId, payload)
          : await this.voyageMaster.create(payload);

        // validasi gagal juga dibalas HTTP 200
        if (response?.status?.error) {
          this.showSaveErrors(response.status.message);
          return;
        }

        this.$toast.success(isUpdate ? 'Successfully updated voyage' : 'Successfully created voyage');
        this.displayForm = false;
        this.getData();
      } catch (e) {
        this.showSaveErrors(e.response?.data?.status?.message);
        console.error('save voyage error : ', e?.message);
      } finally {
        this.isSubmit = false;
      }
    },
    // message bisa object per field ({ mvygName: { required: '...' } }) atau string
    showSaveErrors(message) {
      if (message && typeof message === 'object') {
        const newErrors = {};
        Object.keys(message).forEach((field) => {
          newErrors[field] = Object.values(message[field]).join(' ');
        });
        this.errors = newErrors;
        this.$toast.error('Please check the form for errors.');
        return;
      }
      this.$toast.error(message || 'Something went wrong');
    },
    onDelete(item) {
      this.selectedItem = item;
      this.displayDeletion = true;
    },
    async onConfirmDeletion() {
      try {
        const response = await this.voyageMaster.delete(this.selectedItem.mvygId);
        if (response?.status?.error) {
          this.$toast.error(typeof response.status.message === 'string' && response.status.message
            ? response.status.message
            : 'Failed to delete data');
          return;
        }
        this.$toast.success('Successfully deleted data');
        this.getData();
      } catch (e) {
        this.$toast.error('Failed to delete data');
        console.error('delete voyage error : ', e?.message);
      } finally {
        this.displayDeletion = false;
        this.selectedItem = null;
      }
    },
    onDetail(item) {
      this.$router.push({ name: 'VoyageDetail', params: { id: item.mvygId } });
    },

    openMapSelector(target) {
      this.landmarkSelectionTarget = target;
      this.displayMapModal = true;
      this.selectedMapLandmark = null;
      this.mapModalMode = 'select';
      this.pendingLandmarkLatLng = null;
    },

    // langsung mode tambah
    openLandmarkCreate(target, name) {
      this.landmarkSelectionTarget = target;
      this.selectedMapLandmark = null;
      this.pendingLandmarkLatLng = null;
      this.landmarkErrors = {};
      this.newLandmark = {
        lndmName: name || '',
        lndmType: 'port',
        lndmDescription: '',
        lndmLatitude: '',
        lndmLongitude: '',
      };
      this.mapModalMode = 'add';
      this.displayMapModal = true;
    },

    // pusat peta di ujung satunya
    otherEndpointLatLng(target) {
      const id = target === 'origin' ? this.currentItem.mvygDestinationLndmId : this.currentItem.mvygOriginLndmId;
      if (!id) return null;
      const landmark = this.landmarks.find((l) => String(l.lndmId) === String(id));
      const lat = parseFloat(landmark?.lndmLatitude);
      const lng = parseFloat(landmark?.lndmLongitude);
      return Number.isNaN(lat) || Number.isNaN(lng) ? null : [lat, lng];
    },

    // Koordinat awal diambil dari titik yang barusan diklik; kalau belum ngeklik,
    // pakai tengah peta — itu yang lagi dilihat user.
    openLandmarkForm() {
      const seed = this.pendingLandmarkLatLng
        || (this.mapInstance ? this.mapInstance.getCenter() : null);
      this.landmarkErrors = {};
      this.newLandmark = {
        lndmName: '',
        lndmType: '',
        lndmDescription: '',
        lndmLatitude: seed ? Number(seed.lat).toFixed(6) : '',
        lndmLongitude: seed ? Number(seed.lng).toFixed(6) : '',
      };
      // v-if bakal nyabut div petanya; bongkar dulu biar instance-nya gak nyangkut.
      this.destroyLandmarkMap();
      this.mapModalMode = 'add';
    },

    closeLandmarkForm() {
      this.mapModalMode = 'select';
      this.landmarkErrors = {};
      this.$nextTick(() => this.onMapModalShown());
    },

    async onLandmarkCreated(form) {
      this.landmarkErrors = {};
      this.isSubmittingLandmark = true;
      try {
        const activeCompany = this.$store.getters['auth/activeCompany'];
        const payload = {
          lndmName: form.lndmName,
          lndmDescription: form.lndmDescription,
          lndmType: form.lndmType,
          lndmLatitude: form.lndmLatitude,
          lndmLongitude: form.lndmLongitude,
        };
        if (activeCompany?.compId) payload.lndmCompId = activeCompany.compId;
        const response = await LandmarkSvc.landmarks.create(payload);
        const status = response.data?.status;
        if (status?.error !== 0) {
          const message = status?.message || 'Save failed';
          if (typeof message === 'object') {
            this.landmarkErrors = message;
            this.$toast.error('Error found');
          } else {
            this.$toast.error(message);
          }
          return;
        }
        await this.getLandmarks();
        // Dicari dari daftar yang udah di-refresh pakai nama + koordinat, bukan dari
        // id di respons — bentuk respons create-nya gak dijamin bawa id.
        const sameCoord = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-6;
        const created = this.landmarks.find((l) => l.lndmName === form.lndmName
          && sameCoord(l.lndmLatitude, form.lndmLatitude)
          && sameCoord(l.lndmLongitude, form.lndmLongitude));
        this.$toast.success('Landmark added');
        this.mapModalMode = 'select';
        // Langsung kepilih buat Origin/Destination yang lagi diisi — itu inti alurnya.
        if (created) {
          this.selectedMapLandmark = created;
          this.onLandmarkSelectedFromMap();
        } else {
          this.$nextTick(() => this.onMapModalShown());
        }
      } catch (error) {
        const message = error.response?.data?.status?.message;
        this.$toast.error(typeof message === 'string' ? message : 'Error when submitting form');
      } finally {
        this.isSubmittingLandmark = false;
      }
    },

    async ensureLeafletLoaded() {
      if (typeof L !== 'undefined') return true;

      const existingScript = document.querySelector('script[src="/script/leaflet.js"]');
      if (existingScript) {
        await new Promise((resolve, reject) => {
          if (typeof L !== 'undefined') {
            resolve();
            return;
          }
          existingScript.addEventListener('load', resolve, { once: true });
          existingScript.addEventListener('error', reject, { once: true });
        });
        return typeof L !== 'undefined';
      }

      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/script/leaflet.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return typeof L !== 'undefined';
    },

    initLandmarkMap() {
      const mapContainer = document.getElementById('landmark-selector-map');
      if (!mapContainer) return false;

      this.mapInstance = L.map(mapContainer, {
        zoomControl: true,
      }).setView(this.otherEndpointLatLng(this.landmarkSelectionTarget) || [-5.95, 106.95], 10);
      // eslint-disable-next-line
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.mapInstance);

      this.mapInstance.on('moveend', () => {
        this.fetchMapLandmarks();
      });

      // Klik di ruang kosong nandain calon lokasi landmark baru. Klik marker
      // gak sampai sini karena Leaflet nahan propagasinya.
      this.mapInstance.on('click', (e) => {
        this.pendingLandmarkLatLng = e.latlng;
      });

      this.fetchMapLandmarks();
      return true;
    },

    async fetchMapLandmarks() {
      if (!this.mapInstance) return;
      this.mapLoading = true;
      try {
        const bounds = this.mapInstance.getBounds();
        const { data } = await LandmarkSvc.landmarks.area({
          minLatitude: bounds.getSouth(),
          maxLatitude: bounds.getNorth(),
          minLongitude: bounds.getWest(),
          maxLongitude: bounds.getEast(),
        });
        this.mapLandmarks = data.data || [];
        this.updateMapMarkers();
      } catch (e) {
        console.error('Failed to fetch landmarks for map area', e?.message);
        this.$toast.error('Failed to load landmarks for the visible map area.');
      } finally {
        this.mapLoading = false;
      }
    },

    updateMapMarkers() {
      if (!this.mapInstance) return;

      if (this.markerLayer) {
        this.markerLayer.clearLayers();
      } else {
        // eslint-disable-next-line
        this.markerLayer = L.layerGroup().addTo(this.mapInstance);
      }

      // eslint-disable-next-line
      const defaultIcon = L.icon({
        iconUrl: '/marker/pinpoint.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });

      // eslint-disable-next-line
      const selectedIcon = L.icon({
        iconUrl: '/marker/online-marker.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });

      this.mapLandmarks.forEach((landmark) => {
        const lat = parseFloat(landmark.lndmLatitude);
        const lng = parseFloat(landmark.lndmLongitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        // eslint-disable-next-line
        const marker = L.marker([lat, lng], {
          icon: defaultIcon,
          landmarkId: landmark.lndmId,
        });

        marker.on('click', () => {
          this.selectedMapLandmark = landmark;
          this.mapSearchQuery = landmark.displayName || landmark.lndmName || '';
          this.markerLayer.eachLayer((layer) => layer.setIcon(defaultIcon));
          marker.setIcon(selectedIcon);
        });

        marker.bindPopup(`<b>${landmark.lndmName}</b><br>${landmark.lndmDescription || ''}`);

        this.markerLayer.addLayer(marker);

        if (this.selectedMapLandmark && this.selectedMapLandmark.lndmId === landmark.lndmId) {
          marker.setIcon(selectedIcon);
        }
      });
    },
    onMapSearchInput() {
      this.showMapSearchDropdown = true;
    },
    onMapSearchBlur() {
      this.mapSearchBlurTimeout = setTimeout(() => {
        this.showMapSearchDropdown = false;
      }, 120);
    },
    selectLandmarkFromSearch(landmark) {
      if (!landmark) {
        return;
      }
      this.selectedMapLandmark = landmark;
      this.mapSearchQuery = landmark.displayName || landmark.lndmName || '';
      this.showMapSearchDropdown = false;
      const lat = parseFloat(landmark.lndmLatitude);
      const lng = parseFloat(landmark.lndmLongitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && this.mapInstance) {
        this.mapInstance.setView([lat, lng], 14);
      }
    },

    onLandmarkSelectedFromMap() {
      if (!this.selectedMapLandmark) return;

      const { lndmId, lndmName, lndmCompId } = this.selectedMapLandmark;

      const activeCompId = this.$store.getters['auth/activeCompany'].compId;
      const isCompany = lndmCompId === activeCompId;

      const displayName = isCompany ? `[COMPANY] ${lndmName}` : `[GLOBAL] ${lndmName}`;

      if (this.landmarkSelectionTarget === 'origin') {
        this.$set(this.currentItem, 'mvygOriginLndmId', lndmId);
        this.$set(this.currentItem, 'originDisplayName', displayName);
        // Force update the search query in the child component
        this.$nextTick(() => {
          if (this.$refs.originInput) {
            this.$refs.originInput.searchQuery = displayName;
          }
        });
      } else if (this.landmarkSelectionTarget === 'destination') {
        this.$set(this.currentItem, 'mvygDestinationLndmId', lndmId);
        this.$set(this.currentItem, 'destinationDisplayName', displayName);
        // Force update the search query in the child component
        this.$nextTick(() => {
          if (this.$refs.destinationInput) {
            this.$refs.destinationInput.searchQuery = displayName;
          }
        });
      }

      this.displayMapModal = false;
      this.selectedMapLandmark = null;
    },

    loadLeafletCSS() {
      if (!document.querySelector('link[href="/style/leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/style/leaflet.css';
        document.head.appendChild(link);
      }
    },

    async onMapModalShown() {
      // mode add pakai peta LandmarkForm
      if (this.mapModalMode === 'add') return;
      if (this.mapInitInProgress) return;
      this.mapInitInProgress = true;

      try {
        const leafletReady = await this.ensureLeafletLoaded();
        if (!leafletReady) {
          this.$toast.error('Leaflet library is not loaded.');
          return;
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.destroyLandmarkMap();
            const initialized = this.initLandmarkMap();
            if (!initialized || !this.mapInstance) {
              this.$toast.error('Map container is not ready.');
              return;
            }

            this.mapInstance.invalidateSize();
            setTimeout(() => {
              if (!this.mapInstance) return;
              this.mapInstance.invalidateSize();
              this.fetchMapLandmarks();
            }, 250);
          }, 180);
        });
      } catch (e) {
        console.error('Failed to initialize map modal', e);
        this.$toast.error('Failed to initialize map.');
      } finally {
        this.mapInitInProgress = false;
      }
    },

    onMapModalHidden() {
      this.destroyLandmarkMap();
      this.mapModalMode = 'select';
      this.pendingLandmarkLatLng = null;
      this.selectedMapLandmark = null;
      this.mapSearchQuery = '';
      this.showMapSearchDropdown = false;
      if (this.mapSearchBlurTimeout) {
        clearTimeout(this.mapSearchBlurTimeout);
        this.mapSearchBlurTimeout = null;
      }
    },

    destroyLandmarkMap() {
      if (this.mapInstance) {
        this.mapInstance.off();
        this.mapInstance.remove();
        this.mapInstance = null;
      }
      this.markerLayer = null;
      this.mapLandmarks = [];
    },
  },
  watch: {
    selectedAsset: {
      immediate: true,
      handler() {
        if (this.assets.length > 0 && this.assetsLoaded) {
          this.pagination.current = 1;
          this.getData();
        }
      },
    },
    // eslint-disable-next-line func-names
    'currentItem.mvygCargoStatus': function (newVal) {
      if (newVal === 0) {
        this.$set(this.currentItem, 'mvygCargoType', '-');
      } else if (newVal === 1 && this.currentItem.mvygCargoType === '-') {
        // cuma buang placeholder, ketikan user jangan dihapus
        this.$set(this.currentItem, 'mvygCargoType', '');
      }
    },
  },
  mounted() {
    this.loadLeafletCSS();
    this.getAssets();
    this.getLandmarks();
  },
  beforeDestroy() {
    if (this.mapSearchBlurTimeout) {
      clearTimeout(this.mapSearchBlurTimeout);
      this.mapSearchBlurTimeout = null;
    }
    this.destroyLandmarkMap();
  },
};
</script>

<style scoped>
.map-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1200;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.map-search-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  font-size: 13px;
}

.map-search-item:hover {
  background: #f5f7fb;
}

.landmark-selector-map {
  width: 100%;
  height: 60vh;
  min-height: 420px;
}
</style>
